import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getRatesBase,
  listIdleSettings,
  listRatesK1,
  listRatesK2,
} from '@/api/tariffs'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { ServiceConfirmationDiagram } from '@/components/tariffs/ServiceConfirmationDiagram'
import { RatesBaseTable } from '@/components/tariffs/RatesBaseTable'
import { IdleSettingsTable } from '@/components/tariffs/IdleSettingsTable'
import { RatesK1Table } from '@/components/tariffs/RatesK1Table'
import { RatesK2Table } from '@/components/tariffs/RatesK2Table'

/**
 * Страница «Тарифы» — `/tariffs`. Доступ только `admin` (`AdminOnly`).
 *
 * Содержание (см. §6 TARIFFS_SPEC.md):
 *   1. Диаграмма «Когда подтверждается услуга».
 *   2. Базовые тарифы (5 услуг × 2 стороны).
 *   3. Правила простоя (APPRAISAL / PAYMENT_TRANSFER).
 *   4. К1 — города (CRUD, деактивация вместо удаления).
 *   5. К2 — диапазоны оценки (CRUD, валидация пересечений/дыр).
 *
 * К3 настраивается в карточке компании (см. §6.4) — здесь только подсказка.
 */
export function TariffsPage() {
  const { t } = useTranslation()
  const baseQ = useQuery({
    queryKey: ['tariffs', 'rates-base'],
    queryFn: getRatesBase,
    staleTime: 30_000,
  })
  const idleQ = useQuery({
    queryKey: ['tariffs', 'idle-settings'],
    queryFn: listIdleSettings,
    staleTime: 30_000,
  })
  const k1Q = useQuery({
    queryKey: ['tariffs', 'rates-k1'],
    queryFn: listRatesK1,
    staleTime: 30_000,
  })
  const k2Q = useQuery({
    queryKey: ['tariffs', 'rates-k2'],
    queryFn: listRatesK2,
    staleTime: 30_000,
  })

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('tariffs.title')}
        subtitle={t('tariffs.subtitle')}
      />

      <Section
        title={t('tariffs.confirm.sectionTitle')}
        hint={t('tariffs.confirm.sectionHint')}
      >
        <ServiceConfirmationDiagram />
      </Section>

      <Section
        title={t('tariffs.base.sectionTitle')}
        hint={t('tariffs.base.sectionHint')}
      >
        {baseQ.isLoading ? (
          <Loader centered />
        ) : baseQ.error ? (
          <ErrorBox error={baseQ.error} />
        ) : baseQ.data ? (
          <RatesBaseTable data={baseQ.data} />
        ) : null}
      </Section>

      <Section
        title={t('tariffs.idle.sectionTitle')}
        hint={t('tariffs.idle.sectionHint')}
      >
        {idleQ.isLoading ? (
          <Loader centered />
        ) : idleQ.error ? (
          <ErrorBox error={idleQ.error} />
        ) : idleQ.data ? (
          <IdleSettingsTable rows={idleQ.data} />
        ) : null}
      </Section>

      <Section
        title={t('tariffs.k1.sectionTitle')}
        hint={t('tariffs.k1.sectionHint')}
      >
        {k1Q.isLoading ? (
          <Loader centered />
        ) : k1Q.error ? (
          <ErrorBox error={k1Q.error} />
        ) : (
          <RatesK1Table rows={k1Q.data ?? []} />
        )}
      </Section>

      <Section
        title={t('tariffs.k2.sectionTitle')}
        hint={t('tariffs.k2.sectionHint')}
      >
        {k2Q.isLoading ? (
          <Loader centered />
        ) : k2Q.error ? (
          <ErrorBox error={k2Q.error} />
        ) : (
          <RatesK2Table rows={k2Q.data ?? []} />
        )}
        <p className="mt-3 rounded-xl border border-[color:var(--stryzh-color-border-subtle)] bg-[color:var(--stryzh-color-surface)] px-3 py-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('tariffs.k2.k3Hint')} <strong>{t('tariffs.k2.k3HintPath')}</strong>
        </p>
      </Section>
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <header>
        <h2 className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-xl)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
          {title}
        </h2>
        {hint ? (
          <p className="mt-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {hint}
          </p>
        ) : null}
      </header>
      {children}
    </section>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-xl border border-[color:var(--stryzh-color-accent-orange)] bg-[rgba(247,147,26,0.08)] px-3 py-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)]">
      {t('tariffs.loadError', {
        message: error instanceof Error ? error.message : String(error),
      })}
    </div>
  )
}
